/**
 * Single text item row inside GeneratedTextsPreview
 * For Media Devoted | Created By Ciro Autiero
 */

import { memo } from 'react';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

import {
  SMS_TEXT_MAX_LENGTH,
  isSmsTextOverLimit,
  smsEffectiveGsmLength,
} from '../smsTextLimit';

import { PreviewTextItemChip } from './PreviewTextItemChip';

interface PreviewTextItemProps {
  text: string;
  index: number;
  isSelected: boolean;
  onToggle: (idx: number) => void;
  chip?: boolean;
  lengthLimit?: number;
  lengthSourceText?: string;
  compact?: boolean;
  subtitle?: string | null;
}

export const PreviewTextItem = memo(function PreviewTextItem({
  text,
  index,
  isSelected,
  onToggle,
  chip = false,
  lengthLimit,
  lengthSourceText,
  compact = false,
  subtitle,
}: PreviewTextItemProps) {
  if (chip) {
    return (
      <PreviewTextItemChip
        text={text}
        index={index}
        isSelected={isSelected}
        onToggle={onToggle}
        lengthLimit={lengthLimit}
        lengthSourceText={lengthSourceText}
      />
    );
  }

  const sourceForLimit = lengthSourceText ?? text;
  const overLimit = lengthLimit != null && isSmsTextOverLimit(sourceForLimit);
  const limitCap = lengthLimit ?? SMS_TEXT_MAX_LENGTH;
  const effectiveLen = smsEffectiveGsmLength(sourceForLimit);
  const showDev = import.meta.env.MODE === 'development' && !compact;

  const cbBox = compact ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const checkIcon = compact ? 'h-2 w-2' : 'h-2.5 w-2.5';
  const pad = compact ? 'px-1.5 py-0.5' : 'px-2 py-1.5';
  const gap = compact ? 'gap-1' : 'gap-1.5';
  const textSize = compact ? 'text-[11px]' : 'text-xs';

  const borderCls = overLimit
    ? 'border-destructive/70 bg-destructive/10 ring-1 ring-destructive/25'
    : isSelected
      ? 'border-primary/40 bg-primary/5'
      : 'border-transparent bg-muted/30';
  const cbBorder = overLimit
    ? isSelected
      ? 'border-destructive bg-destructive text-destructive-foreground'
      : 'border-destructive/50'
    : isSelected
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-muted-foreground/40';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onToggle(index)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle(index);
        }
      }}
      className={cn(
        'relative flex cursor-pointer rounded-md border transition-colors',
        pad,
        gap,
        'items-start',
        showDev && 'pb-5 pr-2',
        borderCls,
      )}
    >
      <span className={cn('mt-0.5 flex shrink-0 items-center justify-center rounded-sm border', cbBox, cbBorder)}>
        {isSelected && <Check className={checkIcon} />}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          <span
            className={cn(
              'min-w-0 leading-snug',
              textSize,
              overLimit ? 'text-destructive' : 'text-foreground',
              'break-words',
            )}
          >
            {text}
          </span>
          {overLimit && (
            <span className="shrink-0 rounded bg-destructive/15 px-1 py-0.5 text-[10px] font-semibold text-destructive">
              {effectiveLen}/{limitCap}
            </span>
          )}
        </div>
        {subtitle ? (
          <p className="m-0 text-[10px] leading-snug text-amber-800/90 dark:text-amber-400/90">{subtitle}</p>
        ) : null}
      </div>
      {showDev && (
        <span
          className="pointer-events-none absolute bottom-1 right-1.5 text-[9px] tabular-nums leading-none text-muted-foreground/80"
          aria-hidden
          title="Effective GSM-7 length (merge tokens counted as substituted width)"
        >
          {effectiveLen}
          {lengthLimit != null ? `/${limitCap}` : ''} eff.
        </span>
      )}
    </div>
  );
});
